import type { ComponentProps } from "react";
import { Pagination as PaginationBase } from "@ptsd/uikit";
import { XStack } from "tamagui";

type PaginationBody = ComponentProps<typeof PaginationBase.Body>;

const OFFSET = 3;

export const Pagination: React.FC<
  {
    totalPages: number;
    currentPage: number;
    onPageChange: (val: number) => void;
    withoutNavigationButtons?: boolean;
  } & PaginationBody
> = ({
  totalPages,
  withoutNavigationButtons = false,
  currentPage,
  onPageChange,
  ...rest
}) => {
  if (totalPages === 0) return null;

  return (
    <PaginationBase.Body data-id="pagination-body" {...rest}>
      <PaginationBase.Content data-id="pagination-content" gap={"$4"}>
        {!withoutNavigationButtons && (
          <PaginationBase.Previous
            data-id="pagination-previous"
            disabled={currentPage <= 1}
            onPress={() => onPageChange(currentPage - 1)}
          />
        )}

        <XStack
          gap={"$2"}
          justifyContent="center"
          data-id="pagination-pages"
          alignItems="center"
        >
          {currentPage - 3 >= 2 && (
            <PaginationBase.Ellipsis data-id="pagination-ellipsis" />
          )}

          {currentPage - 3 >= 1 && currentPage - 3 >= currentPage - OFFSET && (
            <PaginationBase.Item>
              <PaginationBase.Link
                onPress={() => onPageChange(currentPage - 3)}
              >
                {currentPage - 3}
              </PaginationBase.Link>
            </PaginationBase.Item>
          )}

          {currentPage - 2 >= 1 && currentPage - 2 >= currentPage - OFFSET && (
            <PaginationBase.Item
              data-id={"pagination-page-" + (currentPage - 2)}
            >
              <PaginationBase.Link
                data-id={"pagination-link-" + (currentPage - 2)}
                onPress={() => onPageChange(currentPage - 2)}
              >
                {currentPage - 2}
              </PaginationBase.Link>
            </PaginationBase.Item>
          )}

          {currentPage - 1 >= 1 && currentPage - 1 >= currentPage - OFFSET && (
            <PaginationBase.Item
              data-id={"pagination-page-" + (currentPage - 1)}
            >
              <PaginationBase.Link
                data-id={"pagination-link-" + (currentPage - 1)}
                onPress={() => onPageChange(currentPage - 1)}
              >
                {currentPage - 1}
              </PaginationBase.Link>
            </PaginationBase.Item>
          )}

          <PaginationBase.Item
            data-id={"pagination-page-active-" + currentPage}
          >
            <PaginationBase.Link
              data-id={"pagination-link-active-" + currentPage}
              active
            >
              {currentPage}
            </PaginationBase.Link>
          </PaginationBase.Item>

          {currentPage + 1 <= totalPages &&
            currentPage + 1 <= currentPage + OFFSET && (
              <PaginationBase.Item
                data-id={"pagination-page-" + (currentPage + 1)}
              >
                <PaginationBase.Link
                  data-id={"pagination-link-" + (currentPage + 1)}
                  onPress={() => onPageChange(currentPage + 1)}
                >
                  {currentPage + 1}
                </PaginationBase.Link>
              </PaginationBase.Item>
            )}

          {currentPage + 2 <= totalPages &&
            currentPage + 2 <= currentPage + OFFSET && (
              <PaginationBase.Item
                data-id={"pagination-page-" + (currentPage + 2)}
              >
                <PaginationBase.Link
                  data-id={"pagination-link-" + (currentPage + 2)}
                  onPress={() => onPageChange(currentPage + 2)}
                >
                  {currentPage + 2}
                </PaginationBase.Link>
              </PaginationBase.Item>
            )}

          {currentPage + 3 <= totalPages &&
            currentPage + 3 <= currentPage + OFFSET && (
              <PaginationBase.Item
                data-id={"pagination-page-" + (currentPage + 3)}
              >
                <PaginationBase.Link
                  data-id={"pagination-link-" + (currentPage + 3)}
                  onPress={() => onPageChange(currentPage + 3)}
                >
                  {currentPage + 3}
                </PaginationBase.Link>
              </PaginationBase.Item>
            )}

          {currentPage + 3 < totalPages && (
            <PaginationBase.Ellipsis data-id="pagination-ellipsis" />
          )}
        </XStack>

        {!withoutNavigationButtons && (
          <PaginationBase.Next
            data-id="pagination-next"
            disabled={currentPage >= totalPages}
            onPress={() => onPageChange(currentPage + 1)}
          />
        )}
      </PaginationBase.Content>
    </PaginationBase.Body>
  );
};
